import moment from 'moment';
import { sortBy, isEqual } from 'lodash';

// This method receives an array of intervals (must have start time and an end
// time attributes), a start time, and an end time. Returns those intervals that
// overlap with the specified times.
export const overlappingIntervals = (intervals, startTime, endTime) =>
  intervals.filter(interval => overlaps(interval.start_time, interval.end_time, startTime, endTime));

// Calculates overlaps, also cosidering open ended scenarios
export const overlaps = (firstStartTime, firstEndTime, startTime, endTime) => {
  // If we have the precense of end times for both items, we use the good old
  // cross-comparison
  if (firstEndTime && endTime) {
    return moment(firstStartTime).isBefore(endTime) && moment(firstEndTime).isAfter(startTime);
  }

  // If the first element has an end time, but the second does not, it overlaps
  // whenever the first element ends after the start of the second
  //            <----------------- E2 ----------------- ...
  // < -------- E1 -------> Overlaps
  //                <------- E1 ------> Overlaps
  if (firstEndTime && !endTime) {
    return moment(firstEndTime).isAfter(startTime);
  }

  // If the first element has no end time, but the second has, it overlaps
  // whenever the second element ends after the start of the first
  //            <----------------- E1 ----------------- ...
  // < -------- E2 -------> Overlaps
  //                <------- E2 ------> Overlaps
  if (!firstEndTime && endTime) {
    return moment(endTime).isAfter(firstStartTime);
  }

  // If both are open ended, they overlap, regardless of which one was first
  return true;
};

export const roundToDay = (startTime, endTime) => {
  startTime = moment(startTime).startOf('day').format();

  endTime = moment(endTime).startOf('day').isSame(endTime)
    ? moment(endTime).format()
    : moment(endTime).add(1, 'day').startOf('day').format();

  return [startTime, endTime];
};

export const activeInterval = (intervals, time) => {
  if (!time) return null;

  time = moment(time);
  return intervals.find(i => time.isBetween(i.start_time, i.end_time, undefined, '[)'));
};

export const isActive = (startTime, endTime = null, time = null) => {
  const timeToCompare = time ? moment(time) : moment();
  return endTime ? timeToCompare.isBetween(startTime, endTime) : timeToCompare.isAfter(startTime);
};

export const isPeriodFullyCovered = (intervals, startTime, endTime) => {
  // add virtual intervals for boundaries of viewing period
  intervals = [
    { start_time: moment(startTime), end_time: moment(startTime) },
    ...intervals,
    { start_time: moment(endTime), end_time: moment(endTime) }
  ];
  intervals = sortBy(intervals, 'start_time');

  let currentInterval = intervals[0];

  return intervals.every((interval, index) => {
    const nextInterval = intervals[index + 1];
    if (!nextInterval) return true; // we reached the last interval: done!
    // if there's a next interval and the next interval start time is after
    // current interval's end time, there is a gap. If there is gap, the
    // function is not fully covered
    if (currentInterval.end_time.isBefore(nextInterval.start_time)) return false;

    // Move the current interval, if the nextInterval covers additional time
    // periods (i.e. its end_time is after current end_time)
    if (currentInterval.end_time.isBefore(nextInterval.end_time)) currentInterval = nextInterval;

    return true;
  });
};

// Returns true if there exists at least 1 pair of intervals which overlap
export const anyOverlappingIntervals = intervals => {
  intervals = sortBy(intervals, 'start_time');

  let currentInterval = intervals[0];

  return intervals.some((interval, index) => {
    const nextInterval = intervals[index + 1];
    if (!nextInterval) return false; // we reached the last interval: done!

    // if there's a next interval and the next interval start time is strictly
    // before the current intervals' end time, there is overlap. If there is
    // overlap, the function is over covered
    return currentInterval.end_time.isAfter(nextInterval.start_time);
  });
};

// Cuts out the period periodStart - periodEnd from the interval. Four
// possibilities:
// - returns no intervals if interval falls fully inside the period
// - returns the interval itself if it falls completely outside the period
// - returns two intervals with the period cut out if the period falls fully
//   inside the interval
// - slices off the start or end if partially overlapping
export const subtractInterval = (interval, periodStart, periodEnd) => {
  // return original interval if slice period is not valid
  if (moment(periodStart).isSameOrAfter(periodEnd)) return [interval];
  // return original interval if slice period ends before interval
  if (moment(interval.start_time).isAfter(periodEnd)) return [interval];
  // return original interval if slice period starts after interval
  if (moment(interval.end_time).isBefore(periodStart)) return [interval];

  const result = [
    {
      ...interval,
      start_time: moment(interval.start_time).clone(),
      end_time: moment(periodStart).clone()
    },
    {
      ...interval,
      start_time: moment(periodEnd).clone(),
      end_time: moment(interval.end_time).clone()
    }
  ];

  // keep all non-empty intervals
  return result.filter(i => i.start_time.isBefore(i.end_time));
};

const iso8601ize = time => {
  if (!time) return time;
  if (typeof time === 'string') return time;
  return moment(time).format();
};

// This method takes two schedules, one existing, and one new and merges them.
// This works as follows. First, a "gap" is cut in the old schedule during the
// period of the new schedule, by removing all intervals that overlap with the
// new schedule. Then, the intervals of the new schedule are transplanted into
// the old schedule. Finally, the gap is "sutured" by merging identical
// consecutive intervals. This is to avoid unnecessary splitting of intervals
// on the edges of the new schedule.
export const mergeSchedules = (oldSchedule, newSchedule, mergeMode) => {
  // ensure all times are strings
  newSchedule = {
    ...newSchedule,
    start_time: iso8601ize(newSchedule.start_time),
    end_time: iso8601ize(newSchedule.end_time),
    intervals: newSchedule.intervals.map(i => ({
      ...i,
      start_time: iso8601ize(i.start_time),
      end_time: iso8601ize(i.end_time)
    }))
  };

  if (!mergeMode) return newSchedule;
  if (!oldSchedule) return newSchedule;

  let resultIntervals = [...oldSchedule.intervals];

  for (const interval of newSchedule.intervals) {
    resultIntervals = insertInterval(resultIntervals, interval);
  }

  resultIntervals = mergeConsecutiveIntervals(resultIntervals);

  const newStartTime = moment.min(moment(oldSchedule.start_time), moment(newSchedule.start_time));
  const newEndTime = moment.max(moment(oldSchedule.end_time), moment(newSchedule.end_time));

  return {
    ...oldSchedule,
    start_time: newStartTime.format(),
    end_time: newEndTime.format(),
    intervals: resultIntervals
  };
};

// merges an array of intervals if they are consecutive, and
// equalsFunction(interval1, interval2) returns true. Default: deep equals of
// everything except the start & end time
export const mergeConsecutiveIntervals = (intervals, equalsFunction = isEqual) => {
  let result = [];
  const withoutTimes = interval => {
    const { start_time, end_time, ...result } = interval;
    return result;
  };

  // compare intervals by content (i.e. everything except start and end time)
  const equalIntervalContent = (interval1, interval2) =>
    equalsFunction(withoutTimes(interval1), withoutTimes(interval2));

  for (const interval of intervals) {
    // We can ignore intervals of zero length
    if (moment(interval.start_time).isSame(interval.end_time)) continue;
    const previous = result.at(-1);
    const consecutive = previous?.end_time === interval.start_time;
    if (consecutive && equalIntervalContent(previous, interval)) {
      // intervals are consecutive and have equal content
      // remove previous and add merged interval
      result.pop();
      result.push({ ...previous, end_time: interval.end_time });
    } else {
      result.push(interval);
    }
  }

  return result;
};

const insertInterval = (intervals, newInterval) => {
  const startTime = moment(newInterval.start_time);
  const endTime = moment(newInterval.end_time);

  const result = [];

  // carve out space for the new interval by looking at each existing interval
  // individually
  for (const interval of intervals) {
    if (startTime.isSameOrAfter(interval.end_time)) {
      // OLD    <=======>
      // NEW              <======>
      result.push(interval); // keep the old interval
    } else if (endTime.isSameOrBefore(interval.start_time)) {
      // OLD              <=======>
      // NEW    <======>
      result.push(interval); // keep the old interval
    } else if (startTime.isAfter(interval.start_time) && endTime.isBefore(interval.end_time)) {
      // OLD <===========>
      // NEW    <=====>
      // RES <=>       <=>
      // old interval is cut in twain
      const interval1 = {
        ...interval,
        end_time: newInterval.start_time
      };
      const interval2 = {
        ...interval,
        start_time: newInterval.end_time
      };

      result.push(interval1, interval2);
    } else if (startTime.isSameOrBefore(interval.start_time) && endTime.isSameOrAfter(interval.end_time)) {
      // OLD     <======>
      // NEW  <=============>
      // old interval is completely overlapped => discard old interval
    } else if (startTime.isSameOrBefore(interval.end_time) && endTime.isSameOrAfter(interval.end_time)) {
      // intersect at the end
      // OLD <======>
      // NEW     <======>
      // RES <==>
      result.push({
        ...interval,
        end_time: newInterval.start_time
      });
    } else if (startTime.isSameOrBefore(newInterval.start_time) && endTime.isSameOrAfter(newInterval.start_time)) {
      // intersect at the start
      // OLD        <======>
      // NEW   <======>
      // RES           <===>
      result.push({
        ...interval,
        start_time: newInterval.end_time
      });
    } else {
      throw 'This should not happen';
    }
  }
  // now that we made space for the new interval, add it to the list
  result.push(newInterval);
  return sortBy(result, interval => moment(interval.start_time));
};

export const splitIntervals = (intervals, time) => {
  // Using reduce to collect all intervals. An interval can either stay
  // unchanged, or be split in 2.
  return intervals.reduce((result, interval) => {
    if (time.isBetween(interval.start_time, interval.end_time)) {
      // split interval into 2 at `time`. Mark them as transient
      const first = { ...interval, end_time: time.format(), transient: true };
      const last = { ...interval, start_time: time.format(), transient: true };
      return result.concat([first, last]);
    } else {
      return result.concat([interval]);
    }
  }, []);
};

// returns a new schedule, with the interval overlapping at `time` being split
// in two, if any
export const splitInterval = (schedule, time) => {
  time = moment(time);

  // Using reduce to collect all intervals. An interval can either stay
  // unchanged, or be split in 2.
  const intervals = splitIntervals(schedule.intervals, time);

  return { ...schedule, intervals };
};

const midnightSplits = (startMoment, endMoment) => {
  let currentMoment = startMoment.clone(); // Clone to avoid mutating the original startMoment
  const end = endMoment;
  const days = [];

  while (currentMoment.isBefore(end, 'day')) {
    currentMoment = currentMoment.clone().add(1, 'day').startOf('day');
    days.push(currentMoment);
  }

  return days;
};

export const splitIntervalsByDay = intervals => {
  if (!intervals || intervals.length === 0) return [];

  let split = intervals;
  const startTime = moment(intervals[0].start_time);
  const endTime = moment(intervals.at(-1).end_time);

  for (const time of midnightSplits(startTime, endTime)) {
    split = splitIntervals(split, time);
  }

  return split;
};
